import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useT } from "../i18n/LanguageContext";
import LanguageSwitcher from "./LanguageSwitcher";

const LINK_IDS = ["live", "invest", "combo", "amenities", "pricing", "attractions", "location"];

export default function Navbar({ onBookTour }) {
    const { t } = useT();
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 20);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const scrollTo = (id) => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        setOpen(false);
    };

    return (
        <motion.header
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className={`sticky top-0 z-40 w-full transition-all duration-500 ${
                scrolled
                    ? "bg-charcoal/80 backdrop-blur-xl border-b border-white/10"
                    : "bg-transparent"
            }`}
            data-testid="site-navbar"
        >
            <div className="mx-auto max-w-7xl px-6 md:px-12 h-20 flex items-center justify-between gap-6">
                <a
                    href="#top"
                    className="group flex items-center gap-3 shrink-0"
                    data-testid="brand-link"
                >
                    <span className="w-8 h-8 rounded-sm bg-gold flex items-center justify-center text-charcoal font-display font-black text-sm">
                        W
                    </span>
                    <div className="leading-none">
                        <div className="font-display text-sm font-bold tracking-wide">
                            {t("brand.title")}
                        </div>
                        <div className="text-[10px] tracking-[0.25em] uppercase text-white/50">
                            {t("brand.subtitle")}
                        </div>
                    </div>
                </a>

                <nav className="hidden lg:flex items-center gap-8">
                    {LINK_IDS.map((id) => (
                        <button
                            key={id}
                            onClick={() => scrollTo(id)}
                            className="text-sm tracking-wide text-white/70 hover:text-white transition-colors relative group"
                            data-testid={`nav-link-${id}`}
                        >
                            {t(`nav.${id}`)}
                            <span className="absolute -bottom-1 left-0 w-0 h-[1px] bg-gold transition-all duration-300 group-hover:w-full" />
                        </button>
                    ))}
                </nav>

                <div className="hidden lg:flex items-center gap-3">
                    <LanguageSwitcher />
                    <Link
                        to="/admin/login"
                        className="text-xs tracking-[0.2em] uppercase text-white/50 hover:text-gold transition-colors"
                        data-testid="nav-admin-link"
                    >
                        {t("nav.admin")}
                    </Link>
                    <button
                        onClick={onBookTour}
                        className="group relative overflow-hidden bg-gold px-5 py-3 text-charcoal text-xs tracking-[0.2em] uppercase font-semibold hover:bg-gold-hover transition-colors whitespace-nowrap"
                        data-testid="nav-book-tour-btn"
                    >
                        {t("nav.book_tour")}
                    </button>
                </div>

                <div className="lg:hidden flex items-center gap-3">
                    <LanguageSwitcher />
                    <button
                        className="text-white"
                        onClick={() => setOpen((v) => !v)}
                        aria-label="Toggle menu"
                        data-testid="mobile-menu-toggle"
                    >
                        {open ? <X size={24} /> : <Menu size={24} />}
                    </button>
                </div>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="lg:hidden bg-charcoal/95 backdrop-blur-xl border-t border-white/10 overflow-hidden"
                        data-testid="mobile-menu"
                    >
                        <div className="px-6 py-6 flex flex-col gap-4">
                            {LINK_IDS.map((id) => (
                                <button
                                    key={id}
                                    onClick={() => scrollTo(id)}
                                    className="text-left text-base font-display text-white/80 hover:text-gold"
                                    data-testid={`mobile-nav-link-${id}`}
                                >
                                    {t(`nav.${id}`)}
                                </button>
                            ))}
                            <button
                                onClick={() => {
                                    onBookTour();
                                    setOpen(false);
                                }}
                                className="mt-2 bg-gold px-6 py-3 text-charcoal text-xs tracking-[0.2em] uppercase font-semibold"
                                data-testid="mobile-book-tour-btn"
                            >
                                {t("nav.book_tour")}
                            </button>
                            <Link
                                to="/admin/login"
                                className="text-xs tracking-[0.2em] uppercase text-white/50 hover:text-gold"
                            >
                                {t("nav.admin")}
                            </Link>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.header>
    );
}
